import { Entity } from 'src/models/entity/entity';

type PrincipalEntityMatcher = (taxId?: string) => boolean;

export function isEntitySharingRoot(
  entity: Entity | null | undefined,
  _isPrincipalEntityTaxId?: PrincipalEntityMatcher,
): boolean {
  return !!entity?.id && entity.isRootDB === true;
}

export function findEntityById(
  entities: Entity[],
  entityId: string,
): Entity | null {
  for (const entity of entities || []) {
    if (entity.id === entityId) {
      return entity;
    }

    const childMatch = findEntityById(entity.children || [], entityId);
    if (childMatch) {
      return childMatch;
    }
  }

  return null;
}

function findEntityLineage(
  entities: Entity[],
  entityId: string,
  lineage: Entity[] = [],
): Entity[] | null {
  for (const entity of entities || []) {
    const nextLineage = [...lineage, entity];

    if (entity.id === entityId) {
      return nextLineage;
    }

    const childLineage = findEntityLineage(
      entity.children || [],
      entityId,
      nextLineage,
    );
    if (childLineage) {
      return childLineage;
    }
  }

  return null;
}

export function resolveEntitySharingRoot(
  entities: Entity[],
  entityId: string,
  isPrincipalEntityTaxId: PrincipalEntityMatcher,
): Entity | null {
  const lineage = findEntityLineage(entities, entityId) || [];

  for (let index = lineage.length - 1; index >= 0; index -= 1) {
    const candidate = lineage[index];
    if (isEntitySharingRoot(candidate, isPrincipalEntityTaxId)) {
      return candidate;
    }
  }

  return lineage[0] || null;
}

export function buildEntitySharingTree(
  rootEntity: Entity | null | undefined,
): Entity | null {
  if (!rootEntity?.id) {
    return null;
  }

  return cloneEntityForSharingTree(rootEntity, true);
}

function cloneEntityForSharingTree(
  entity: Entity,
  isRootNode: boolean = false,
): Entity | null {
  if (!entity?.id) {
    return null;
  }

  if (!isRootNode && isEntitySharingRoot(entity)) {
    return null;
  }

  const children = (entity.children ?? [])
    .map((child) => cloneEntityForSharingTree(child))
    .filter((child): child is Entity => !!child);

  return {
    ...entity,
    children,
  };
}
